// ============================================================================
// stage1-batch-classify-run.ts: the AI batch classification of imported
// documents (RFQ, inspector notes, reports), moved out of Stage1DataEntry so a
// Quote Run classifies its documents exactly as a Stage 1 drop does (Peter,
// 2026-10-23: the two routes must behave the same). The body is the Stage 1
// closure unchanged; only the import-abort ref became `shouldStop`.
// ============================================================================

import { getConfigValue, getPrompt } from '../config/pipeline-registry';
import { BATCH_CLASSIFY_PROMPT } from '../config/prompts';
import { callOpenRouter, getMaxOutputTokens } from './ai-api';
import { DOCUMENT_CLASSIFICATION_MODEL_TASK, pickModelForTask } from './model-selector';
import { detectRfqByContent } from '../utils/rfq-content-detector';
import {
    applyStage1BatchClassification,
    applyStage1BatchRfqFallback,
    buildStage1BatchClassifyDocumentList,
    selectStage1RfqFallbackCandidate,
} from './stage1-batch-classification';

/**
 * Classify every document in one AI call and write the result onto each one
 * (`type`, `aiClassification`, its confidence and reason). Returns the same
 * array. A failed call leaves the deterministic types in place.
 */
export async function classifyStage1DocumentsBatch(docs: any[], { apiKey, model, shouldStop = () => false }: {
    apiKey: string | null | undefined;
    model: string | null | undefined;
    shouldStop?: () => boolean;
}): Promise<any[]> {
    // Classify any number of docs ≥ 1 so single-doc imports also get
    // routed (previously short-circuited when docs.length < 2).
    if (!apiKey || !docs || docs.length < 1) return docs;
    if (shouldStop()) return docs;

    const summaryLen = getConfigValue('batch-classify', 'summaryLength') ?? 500;
    // CN5: the model sees each document's position and format, never
    // its file name. Its answer is read back by that position (c.index).
    const docList = buildStage1BatchClassifyDocumentList(docs, summaryLen);

    try {
        const template = getPrompt('batch-classify') || BATCH_CLASSIFY_PROMPT;
        const prompt = template.replace('${documentList}', docList);
        const classificationModel = pickModelForTask(DOCUMENT_CLASSIFICATION_MODEL_TASK, model);

        const result = await callOpenRouter({
            apiKey, model: classificationModel,
            messages: [{ role: 'user', content: prompt }],
            temperature: getConfigValue('batch-classify', 'temperature') ?? 0.1,
            // One response accounts for every imported document.
            // A fixed 4k cap truncated known 27-50 file batches and
            // made intake appear complete with missing rows.
            maxTokens: getMaxOutputTokens(classificationModel),
            responseFormat: { type: 'json_object' },
            pipelineId: DOCUMENT_CLASSIFICATION_MODEL_TASK,
            task: DOCUMENT_CLASSIFICATION_MODEL_TASK,
            stageName: '1'
        } as any);

        // G-001 (2026-04-25): prompt now emits
        //   { rfq_present: boolean, classifications: [...] }
        // Prefer the object shape; fall back to the legacy bare
        // array so in-flight responses from any cached prompt
        // override still parse cleanly.
        let classifications = null;
        let rfqPresentSignal = null; // tri-state: true | false | null (unknown)
        const objectMatch = result.match(/\{[\s\S]*\}/);
        if (objectMatch) {
            try {
                const parsed = JSON.parse(objectMatch[0]);
                if (parsed && Array.isArray(parsed.classifications)) {
                    classifications = parsed.classifications;
                    if (typeof parsed.rfq_present === 'boolean') {
                        rfqPresentSignal = parsed.rfq_present;
                    }
                }
            } catch { /* fall through to legacy bare-array parse */ }
        }
        if (!classifications) {
            const arrayMatch = result.match(/\[[\s\S]*\]/);
            if (arrayMatch) {
                try {
                    const parsedArr = JSON.parse(arrayMatch[0]);
                    if (Array.isArray(parsedArr)) classifications = parsedArr;
                } catch { /* ignore */ }
            }
        }
        if (classifications) {
            console.groupCollapsed('[QM] Batch Document Classification');
            // v5.12.15 (Peter 2026-04-22, Francis pilot): a document
            // pre-tagged 'rfq' keeps that class over a low-confidence AI
            // reclassification. CN2 (Peter 2026-10-13: "file names
            // shouldnt dictate anything. only the contents."): what
            // keeps it is its text reading as the request
            // (detectRfqByContent), never its file name.
            classifications.forEach((c: any) => {
                if (c.index >= 0 && c.index < docs.length) {
                    const d = docs[c.index];
                    const textReadsAsRfq = detectRfqByContent(d.content).isRfq;
                    const aiWantsOther = c.classification === 'other';
                    const lowConfidenceOverride = (c.confidence || 0) < 75;
                    const wasPreTaggedRfq = d.type === 'rfq';
                    // Preserve a pre-set 'rfq' when:
                    //   (a) its text reads as the request, OR
                    //   (b) the AI wants to demote it to 'other' with low confidence
                    const preserveRfq = wasPreTaggedRfq
                        && (textReadsAsRfq || (aiWantsOther && lowConfidenceOverride));
                    const applied = applyStage1BatchClassification(d, {
                        candidateClassification: c.classification,
                        candidateConfidence: c.confidence,
                        candidateReason: c.reason,
                        preserveRfq,
                    });
                    if (!applied) return;
                    console.log(`  ${d.name} \u2192 ${applied.finalClass} (${applied.confidence}%)${preserveRfq ? ' [CONTENT KEPT RFQ]' : ''} - ${applied.reason}`);
                }
            });
            // v5.12.15: guarantee exactly one RFQ when one is
            // actually present. If the AI pass removed the RFQ
            // label from every doc, promote the one whose text
            // reads as the request (CN2).
            //
            // G-001 (2026-04-25): when the prompt explicitly
            // signals rfq_present=false, honour that — the AI
            // reviewed the batch and decided no document is an
            // RFQ. Only auto-promote when the signal is absent
            // (legacy bare-array response) OR the signal says
            // RFQ should be present but a classification slip
            // dropped the label.
            const hasRfq = docs.some(d => d.type === 'rfq');
            const shouldAutoPromote = !hasRfq && rfqPresentSignal !== false;
            if (shouldAutoPromote) {
                const candidate = selectStage1RfqFallbackCandidate(docs);
                if (candidate) {
                    applyStage1BatchRfqFallback(candidate);
                    console.log(`  [fallback promotion] ${candidate.name} \u2192 rfq`);
                }
            } else if (!hasRfq && rfqPresentSignal === false) {
                console.log('  [rfq_present=false] Batch has no RFQ per AI; skipping auto-promotion');
            }
            console.groupEnd();
        }
    } catch (err) {
        console.error('Batch classification failed, using filename-based types:', err);
    }
    return docs;
}
